import { useState } from 'react'

import clsx from 'clsx'

import { env } from '@/env'
import { checkInFromQrResolution } from '@/features/partners/lib/checkInFromQrResolution'
import { notifyPartnerCheckIn } from '@/features/partners/lib/notifyPartnerCheckIn'
import { useDigitalProductContextOptional } from '@/shared/contexts/DigitalProductContext'
import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'
import { useCheckIn } from '@/shared/hooks/useCheckIn'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { showErrorAlert } from '@/shared/lib/errors'
import {
  resolveQrCode,
  navigateFromQrResolution,
  isBonusResolution,
  getUnavailableAlert,
  QR_READING_DIALOG_SOURCE
} from '@/shared/lib/qr-resolver'
import { alert, clearDialogs } from '@/shared/ui/dialog'
import { QrCodeReader } from '@/shared/ui/QrCodeReader'

const QR_CODES = {
  YCE8FU: {
    href: '/esn-game-carreiras/missoes/0d3eac55-fb4a-47c5-bc64-bb4299c6f129'
  },
  '6BNSWE': {
    href: '/esn-game-carreiras/missoes/3bc19f18-7056-4e89-96e3-c6b83707df3c'
  },
  JHQ7VX: {
    href: '/esn-game-carreiras/missoes/884764a6-66dc-481b-9cc8-2e074f97debf'
  }
}

const NOT_FOUND_ALERT = {
  variant: 'info',
  title: 'QR code não reconhecido',
  description:
    'Não encontramos um destino para este código. Confira se ele ainda é válido ou tente novamente.',
  source: QR_READING_DIALOG_SOURCE
}

export const handleQrResolution = (resolution, onHide, router, navigateTo) => {
  if (resolution.status === 'resolved') {
    onHide({
      callback: () =>
        navigateFromQrResolution({
          resolution,
          pathname: router.pathname,
          router,
          navigateTo
        })
    })
    return
  }

  if (resolution.status === 'unavailable') {
    onHide({
      callback: () => {
        if (!isBonusResolution(resolution.resourceType)) {
          navigateFromQrResolution({
            resolution,
            pathname: router.pathname,
            router,
            navigateTo
          })
        }

        void alert({
          ...getUnavailableAlert(resolution),
          source: QR_READING_DIALOG_SOURCE
        })
      }
    })
    return
  }

  if (resolution.status === 'technical_error') {
    onHide({ callback: () => showErrorAlert(resolution.error) })
    return
  }

  onHide({ callback: () => void alert(NOT_FOUND_ALERT) })
}

export const BottomNavQrCodeReader = ({ onHide }) => {
  const router = useRouter()
  const { navigateTo } = useNavigation()
  const { initCheckInFromUrl } = useCheckIn()
  // Optional porque é a variante do chrome: a bottomnav monta antes do evento,
  // e o slug já existe nesse intervalo — é prop do provider, não dado
  // carregado. `handleQrResolution` é função de módulo e não pode lê-lo.
  const eventSlug = useDigitalProductContextOptional()?.eventSlug
  const { partnerWasFinished } = useUserProgress()
  const [isLoading, setIsLoading] = useState(false)

  // Registra e devolve o desfecho — **sem** confirmar. A confirmação é um
  // diálogo, e ele só aparece depois que `handleQrResolution` fecha a folha e
  // navega; emitido antes, competiria com a folha ainda aberta.
  // Devolve o desfecho e se o check-in **já estava** feito — este segundo dado
  // sai do progresso em cache e precisa ser lido antes do registro, porque
  // depois dele os dois casos ficam iguais.
  const registerPartnerCheckIn = async resolution => {
    const alreadyDone =
      resolution.status === 'resolved' &&
      resolution.resourceType === 'Partner' &&
      typeof resolution.resource.id === 'number' &&
      partnerWasFinished(resolution.resource.id)

    return [await checkInFromQrResolution(resolution, eventSlug), alreadyDone]
  }

  const handleSuccess = async data => {
    if (isLoading) return
    setIsLoading(true)

    // Leitura nova é intenção nova: o diálogo da anterior não pode aparecer
    // no lugar do desta. Só o que esta leitura enfileirou — descartar a fila
    // inteira responderia por pergunta de fluxo alheio.
    clearDialogs(QR_READING_DIALOG_SOURCE)

    try {
      if (data.includes(env.NEXT_PUBLIC_EVNTTZ_CODES_BASE_URL)) {
        const code = extractUid(data)
        const resolution = code
          ? await resolveQrCode(code)
          : { status: 'not_found' }
        const [checkIn, alreadyDone] = await registerPartnerCheckIn(resolution)
        handleQrResolution(resolution, onHide, router, navigateTo)
        notifyPartnerCheckIn(checkIn, resolution, alreadyDone)
      } else if (data.includes('check_in_code')) {
        await initCheckInFromUrl(data)
        onHide()
      } else if (data.includes('http')) {
        onHide({ callback: () => router.push(data) })
      } else if (QR_CODES[data]) {
        const { href } = QR_CODES[data]
        onHide({ callback: () => router.push(`${href}?qr_code=${data}`) })
      } else {
        const resolution = await resolveQrCode(data)
        const [checkIn, alreadyDone] = await registerPartnerCheckIn(resolution)
        handleQrResolution(resolution, onHide, router, navigateTo)
        notifyPartnerCheckIn(checkIn, resolution, alreadyDone)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // O registro do check-in espera pela resposta mais pesada do app
  // (`Progress::ShowSerializer`). `isLoading` só desabilitava o leitor, o que
  // na fila do estande se parece com tela travada.
  //
  // O contêiner da live region é montado sempre e só o conteúdo alterna: leitor
  // de tela costuma não anunciar região inserida no mesmo tick em que o texto
  // aparece. Vazio, ele não pega toque nem tapa a câmera.
  //
  // `text-base` porque o aviso é a única fonte da informação — piso de corpo de
  // `design-system/ACCESSIBILITY.md § 2.2` é 16px, e `text-sm` fica abaixo.
  return (
    <QrCodeReader
      onSuccess={handleSuccess}
      disabled={isLoading}
      overlay={
        <div
          role="status"
          className={clsx(
            'absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center text-white',
            isLoading ? 'bg-black/70' : 'pointer-events-none'
          )}
        >
          {isLoading ? (
            <>
              <span
                className="inline-block w-6 h-6 rounded-full border-2 border-current border-t-transparent animate-spin"
                aria-hidden
              />
              <span className="text-base font-semibold">
                Validando o código…
              </span>
            </>
          ) : null}
        </div>
      }
    />
  )
}

const extractUid = url => {
  const pathname = new URL(url).pathname
  const uid = pathname.split('/').filter(Boolean).pop()
  return uid
}
